import React from 'react';
import Swal from 'sweetalert2';
import queryString from 'query-string';
import CoupleGroupRegistration from '../../containers/groups/registration/groupRegistration';
import LeaderSelect from '../../containers/groups/registration/leaderSelect';
import GroupSummary from '../../containers/groups/registration/groupSummary';
import MemberLogGroup from '../../containers/groups/registration/memberLogGroup';
import GroupPreferenceSelect from '../../containers/groups/registration/groupPreferenceSelect';
import { GroupsRegistrationComponent } from '../../assets/styles/components/groupsRegistration';
import {
  isCaptchaRequired,
  useRecaptchaToken,
  RECAPTCHA_SECURITY_ERROR,
} from 'src/components/helpers/recaptchaUtils';
import { fetchGroupAttendant } from 'src/containers/groups/registration/groupAttendantLookup';
import {
  getNewMemberActivityName,
  getNewMemberCategoryInfo,
  hasMixedGroupOption,
  isCoupleCivilStatus,
} from 'src/containers/adminDashboard/utils/groupsTranslationMap';

export const groupTitle = (params) => {
  if (window.location.pathname.includes('crecer')) {
    return (
      <>
        <h1>¡Bienvenido al seminario de {params?.seminary.replace(/_/g, ' ')}!</h1>
        <p className="flow-intro">Continúa con tus datos para unirte a un grupo.</p>
      </>
    );
  }
  return <h1>Esta es la información de tu grupo</h1>;
};

const CATEGORY_ERROR =
  'No pudimos determinar tu categoría de grupo. Verifica género, fecha de nacimiento y estado civil.';

const GroupReservation = (props) => {
  const parsedQueryString = React.useMemo(
    () => queryString.parse(props.location?.search || window.location.search),
    [props.location?.search]
  );

  const [Step, setStep] = React.useState('check');
  const [inputs, setInputs] = React.useState(() => ({
    ...(parsedQueryString.identification
      ? { identification: parsedQueryString.identification }
      : {}),
    ...(parsedQueryString.phone ? { phone: parsedQueryString.phone } : {}),
  }));
  const [loading, set_loading] = React.useState(false);
  const [groupPreference, setGroupPreference] = React.useState('');
  const [preferenceError, setPreferenceError] = React.useState(false);
  const params = props.match.params;
  const isCrecerRegistration = Boolean(params?.seminary);
  const getCaptchaToken = useRecaptchaToken('group_login');

  const person1Civil = inputs.person1?.civil || inputs.civil;

  const categoryInfo = getNewMemberCategoryInfo({
    gender: inputs.person1?.gender,
    birthdate: inputs.person1?.birthdate,
    civil: person1Civil,
  });

  const continueFromPreference = () => {
    if (!groupPreference) {
      setPreferenceError(true);
      return;
    }

    const preferMixed = groupPreference === 'mixed';
    const preferCouples = groupPreference === 'couples';
    const activityName = getNewMemberActivityName({
      gender: inputs.person1?.gender,
      birthdate: inputs.person1?.birthdate,
      civil: person1Civil,
      preferMixed,
      preferCouples,
    });

    if (!activityName) {
      Swal.fire(CATEGORY_ERROR);
      return;
    }

    setPreferenceError(false);
    setInputs({
      ...inputs,
      activityName,
      kind: 'Grupo_Discipulado',
      kindgroup: 'Grupo_Discipulado',
      preferMixed,
      preferCouples,
    });

    if (preferCouples && !inputs.person2?.identification) {
      setStep('person2');
      return;
    }

    setStep('selectLeaders');
  };

  const getSelectLeadersBackStep = () => {
    if (
      hasMixedGroupOption({
        gender: inputs.person1?.gender,
        birthdate: inputs.person1?.birthdate,
        civil: person1Civil,
      })
    ) {
      return 'groupPreference';
    }
    if (inputs.activityName?.includes('Parejas') && inputs.person2?.identification) {
      return 'person2';
    }
    return 'person1';
  };

  const checkGroupGuest = async ({
    identification = inputs.identification,
    phone = inputs.phone,
    person = 'person1',
    updateStep = false,
    nextStep = 'person1',
  } = {}) => {
    set_loading(true);
    const captchaRequired = isCaptchaRequired();
    const captcha = await getCaptchaToken({ skip: !captchaRequired });
    if (captchaRequired && !captcha) {
      set_loading(false);
      Swal.fire(RECAPTCHA_SECURITY_ERROR);
      return null;
    }

    try {
      const response = await fetchGroupAttendant({
        inputs,
        identification,
        phone,
        activityName: params?.seminary || inputs.activityName,
        captcha,
        captchaRequired,
      });
      if (!response.data.success) return null;

      const data = response.data;
      setInputs((prev) => {
        let next = { ...prev };
        if (updateStep) {
          next = {
            ...next,
            identification,
            phone,
            activityName: params?.seminary || prev.activityName,
            ...(params?.seminary ? { kind: 'Grupo_Seminario', kindgroup: 'Grupo_Seminario' } : {}),
          };
          if (data.groupMember && data.Attendants) {
            next = {
              ...next,
              Attendants: data.Attendants,
              groupMember: data.groupMember,
            };
          } else if (data.guest) {
            next = { ...next, person1: data.guest, civil: data.guest.civil };
          }
          return next;
        }

        if (data.guest) {
          next = {
            ...next,
            ...(person === 'person1' ? { identification, phone, civil: data.guest.civil } : {}),
            [person]: {
              ...(prev?.[person] || {}),
              ...data.guest,
              identification,
              phone,
            },
          };
        }
        return next;
      });

      if (updateStep) {
        setStep(data.groupMember && data.Attendants ? 'summary' : nextStep);
      }

      return data;
    } catch (error) {
      if (error.response) Swal.fire(error.response.data.message);
      return null;
    } finally {
      set_loading(false);
    }
  };

  const stepRender = () => {
    if (Step === 'check') {
      return (
        <MemberLogGroup
          setInputs={setInputs}
          parsedQueryString={parsedQueryString}
          inputs={inputs}
          next="person1"
          loading={loading}
          checkGroupGuest={checkGroupGuest}
        />
      );
    } else if (Step === 'person1') {
      return (
        <CoupleGroupRegistration
          setInputs={setInputs}
          inputs={inputs}
          person="person1"
          next="person2"
          Step={Step}
          setStep={setStep}
          onGuestUpdated={(credentials) => checkGroupGuest({ ...credentials, person: 'person1' })}
        />
      );
    } else if (Step === 'person2') {
      return (
        <CoupleGroupRegistration
          setInputs={setInputs}
          inputs={inputs}
          person="person2"
          next="selectLeaders"
          Step={Step}
          setStep={setStep}
          onGuestUpdated={(credentials) => checkGroupGuest({ ...credentials, person: 'person2' })}
        />
      );
    } else if (Step === 'groupPreference') {
      return (
        <GroupPreferenceSelect
          gender={inputs.person1?.gender}
          includeCouples={isCoupleCivilStatus(person1Civil)}
          categoryInfo={categoryInfo}
          groupPreference={groupPreference}
          preferenceError={preferenceError}
          onSelectPreference={(value) => {
            setGroupPreference(value);
            setPreferenceError(false);
          }}
          onContinue={continueFromPreference}
          onBack={isCrecerRegistration ? undefined : () => setStep('person1')}
        />
      );
    } else if (Step === 'selectLeaders') {
      return (
        <LeaderSelect
          setInputs={setInputs}
          inputs={inputs}
          next="summary"
          setStep={setStep}
          backStep={isCrecerRegistration ? null : getSelectLeadersBackStep()}
        />
      );
    } else if (Step === 'summary') {
      return (
        <GroupSummary
          match={props.match}
          setInputs={setInputs}
          inputs={inputs}
          next="summary"
          setStep={setStep}
          onRestart={() => {
            setStep('check');
            setInputs({});
            setGroupPreference('');
            setPreferenceError(false);
          }}
        />
      );
    }
  };

  const hideTitle = Step === 'selectLeaders' || Step === 'groupPreference';

  return (
    <GroupsRegistrationComponent>
      {!hideTitle && groupTitle(params)}
      {stepRender()}
    </GroupsRegistrationComponent>
  );
};

export default GroupReservation;
